"use client";

import React, { useState } from 'react';
import { authService, adminService, analyticsService, generateService } from '../services/api';

interface TestResult {
  endpoint: string;
  status: 'success' | 'error' | 'pending';
  data?: any;
  error?: string;
}

export default function ApiTestPanel() {
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUsingMock, setIsUsingMock] = useState(false);

  const addResult = (endpoint: string, status: 'success' | 'error' | 'pending', data?: any, error?: string, isMock?: boolean) => {
    setTestResults(prev => [...prev.filter(r => r.endpoint !== endpoint), { endpoint, status, data, error }]);
    if (isMock) {
      setIsUsingMock(true);
    }
  };

  const runTests = async () => {
    setIsLoading(true);
    setTestResults([]);

    // Testar Analytics
    try {
      addResult('/analytics/sales', 'pending');
      const salesData = await analyticsService.sales();
      addResult('/analytics/sales', 'success', salesData);
    } catch (error: any) {
      addResult('/analytics/sales', 'error', null, error.message);
    }

    // Testar Admin Users
    try {
      addResult('/admin/users', 'pending');
      const usersData = await adminService.getUsers();
      addResult('/admin/users', 'success', usersData);
    } catch (error: any) {
      addResult('/admin/users', 'error', null, error.message);
    }

    // Testar Generate Pitch
    try {
      addResult('/generate/pitch', 'pending');
      const pitchData = await generateService.pitch({
        prompt: "Venda de software de gestão",
        tone: "profissional",
        audience: "empresas"
      });
      addResult('/generate/pitch', 'success', pitchData);
    } catch (error: any) {
      addResult('/generate/pitch', 'error', null, error.message);
    }

    setIsLoading(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600 bg-green-100';
      case 'error': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'pending': return '⏳';
      default: return '❓';
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-semibold">🧪 Painel de Testes da API</h3>
          {isUsingMock && (
            <div className="mt-2 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium inline-flex items-center gap-1">
              ⚠️ Modo Mock (API indisponível)
            </div>
          )}
        </div>
        <button
          onClick={runTests}
          disabled={isLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Testando...' : 'Rodar Testes'}
        </button>
      </div>

      <div className="space-y-3">
        {testResults.map((result) => (
          <div
            key={result.endpoint}
            className={`p-4 rounded-lg border ${getStatusColor(result.status)}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium flex items-center gap-2">
                {getStatusIcon(result.status)}
                {result.endpoint}
              </span>
              <span className="text-sm font-semibold uppercase">
                {result.status}
              </span>
            </div>
            
            {result.data && (
              <details className="mt-2">
                <summary className="cursor-pointer text-sm font-medium">Ver resposta</summary>
                <pre className="mt-2 p-2 bg-gray-50 rounded text-xs overflow-auto max-h-40">
                  {JSON.stringify(result.data, null, 2)}
                </pre>
              </details>
            )}
            
            {result.error && (
              <div className="mt-2 text-sm text-red-700">
                <strong>Erro:</strong> {result.error}
              </div>
            )}
          </div>
        ))}
      </div>

      {testResults.length === 0 && !isLoading && (
        <div className="text-center py-8 text-gray-500">
          Clique em "Rodar Testes" para verificar a conectividade com a API
        </div>
      )}
    </div>
  );
}
